/**
 * Prolific study mode — the contract between the `/study` page and the server.
 *
 * Study mode is additive. Nothing in this file is read by the friends flow
 * (landing page, invite-gated rooms, `/r/CODE`, solo): a participant arrives
 * from Prolific with three URL parameters, works through consent, the tutorial
 * and a microphone check on their own, waits to be paired, and then plays three
 * ordinary rounds in a room the server opened for them. The rounds themselves
 * use the same `joinRoom`-era events as any other room; what lives here is the
 * part around them.
 *
 * Identity rule, as everywhere: a participant is keyed by an internal random
 * `participantId`. The Prolific PID is held by the server's study store and the
 * admin pages, and never appears in a session log, a manifest or any response
 * an unauthenticated caller can read about somebody else.
 */

import type { ConsentRecord, PlayerId } from './index'

// ---------------------------------------------------------------------------
// Entry: the three URL parameters Prolific substitutes
// ---------------------------------------------------------------------------

/** The client route. */
export const STUDY_PATH = /^\/study\/?$/

/** A real Prolific participant id: 24 alphanumerics. */
export const PROLIFIC_PID_PATTERN = /^[a-zA-Z0-9]{24}$/

/** `STUDY_ID` and `SESSION_ID`. */
export const STUDY_FIELD_PATTERN = /^[a-zA-Z0-9_\-:]{1,64}$/

/**
 * A rehearsal id. Accepted only when the server allows test PIDs
 * (`STUDY_ALLOW_TEST_PIDS`), and a test PID's record is reset on re-entry
 * rather than answered with its old code.
 */
export const TEST_PID_PATTERN = /^(TEST_|TEST-|test-)[A-Za-z0-9_-]{1,48}$/

export function isTestPid(pid: string): boolean {
  return TEST_PID_PATTERN.test(pid)
}

export interface StudyParams {
  prolificPid: string
  studyId: string
  /** Prolific's `SESSION_ID` — one per attempt. */
  sessionId: string
}

export type StudyParamsVerdict =
  | { ok: true; params: StudyParams; test: boolean }
  | { ok: false; reason: 'missing' | 'malformed' | 'test_pid_refused' }

/**
 * The one validation both sides run.
 *
 * Values are trimmed first: a link pasted with a trailing space is still the
 * participant's link. A test PID that the server does not allow is its own
 * reason, so the page can say "test links are switched off here" rather than
 * "your link is broken".
 */
export function checkStudyParams(
  raw: { prolificPid?: unknown; studyId?: unknown; sessionId?: unknown },
  allowTestPids: boolean,
): StudyParamsVerdict {
  const read = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
  const prolificPid = read(raw.prolificPid)
  const studyId = read(raw.studyId)
  const sessionId = read(raw.sessionId)
  if (!prolificPid || !studyId || !sessionId) return { ok: false, reason: 'missing' }
  if (!STUDY_FIELD_PATTERN.test(studyId) || !STUDY_FIELD_PATTERN.test(sessionId)) {
    return { ok: false, reason: 'malformed' }
  }
  const test = isTestPid(prolificPid)
  if (test) {
    if (!allowTestPids) return { ok: false, reason: 'test_pid_refused' }
  } else if (!PROLIFIC_PID_PATTERN.test(prolificPid)) {
    return { ok: false, reason: 'malformed' }
  }
  return { ok: true, params: { prolificPid, studyId, sessionId }, test }
}

/** Read the three parameters off a query string, untrimmed and unvalidated. */
export function readStudyQuery(search: string): {
  prolificPid: string | null
  studyId: string | null
  sessionId: string | null
} {
  const query = new URLSearchParams(search)
  return {
    prolificPid: query.get('PROLIFIC_PID'),
    studyId: query.get('STUDY_ID'),
    sessionId: query.get('SESSION_ID'),
  }
}

// ---------------------------------------------------------------------------
// Stages, phases and outcomes
// ---------------------------------------------------------------------------

/**
 * How far a participant got, for pay. Monotonic and persisted.
 *
 *   0  nothing payable yet
 *   1  consent + tutorial + microphone check done
 *   2  game 1 played
 *   3  game 2 played
 *   4  game 3 played and the survey submitted
 */
export type StudyStage = 0 | 1 | 2 | 3 | 4

export const STUDY_STAGES: readonly StudyStage[] = [0, 1, 2, 3, 4]

/** Where in the flow a participant currently is, as the server sees it. */
export type StudyPhase = 'consent' | 'tutorial' | 'mic' | 'waiting' | 'game' | 'survey' | 'done'

/** The phases with an activity-based idle timer. */
export type StudyIdlePhase = 'consent' | 'tutorial' | 'mic' | 'survey'

/** Why a participant's session ended. Every one of these is terminal. */
export type StudyOutcome =
  | 'completed'
  | 'no_consent'
  | 'device_incompatible'
  | 'voice_check_failed'
  | 'idle'
  | 'unmatched'
  | 'dropped'

export const STUDY_OUTCOMES: readonly StudyOutcome[] = [
  'completed',
  'no_consent',
  'device_incompatible',
  'voice_check_failed',
  'idle',
  'unmatched',
  'dropped',
]

/** A participant record's status: still going, or how it ended. */
export type StudyStatus = 'active' | StudyOutcome

/** Where a stage code sends a participant. */
export const PROLIFIC_COMPLETE_URL = 'https://app.prolific.com/submissions/complete?cc='

export function prolificCompletionUrl(code: string): string {
  return `${PROLIFIC_COMPLETE_URL}${encodeURIComponent(code)}`
}

/** Prolific's own home, for the "your link is incomplete" page. */
export const PROLIFIC_HOME_URL = 'https://app.prolific.com/'

/**
 * The study consent wording's version. Separate from the friends-mode
 * `CONSENT_VERSION`, which this never bumps: a study consent record carries
 * `variant: 'study'` and this number.
 *
 *   1  the first wording
 *   2  adds how long the data is kept (`consent.retention` in `study.json`)
 *      and that withdrawing deletes the recordings and data
 */
export const STUDY_CONSENT_VERSION = 2

// ---------------------------------------------------------------------------
// Survey
// ---------------------------------------------------------------------------

export type StudySurveyQuestion =
  | {
      id: string
      text: string
      type: 'likert'
      required: boolean
      scale: { min: number; max: number; minLabel: string; maxLabel: string }
    }
  | {
      id: string
      text: string
      type: 'multiple_choice'
      required: boolean
      options: string[]
      /**
       * Choosing this option asks for a free-text answer too, stored under
       * `${id}_other` and required whenever the option is chosen.
       */
      otherOption?: string
    }
  | { id: string; text: string; type: 'text'; required: boolean; maxLength?: number }

export type StudySurveyAnswers = Record<string, string | number>

/** The longest free-text answer kept, whatever a question says. */
export const MAX_SURVEY_TEXT = 2000

/**
 * The answers worth storing, or `null` if a required one is missing.
 *
 * One function for both sides: the client disables Submit on `null`, and the
 * server refuses to store one. Unknown keys are dropped, text is clipped, a
 * Likert value must be an integer on its scale and a choice must be one of the
 * options.
 */
export function cleanSurveyAnswers(
  questions: readonly StudySurveyQuestion[],
  raw: unknown,
): StudySurveyAnswers | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const given = raw as Record<string, unknown>
  const out: StudySurveyAnswers = {}
  for (const q of questions) {
    const value = given[q.id]
    if (q.type === 'likert') {
      const ok =
        typeof value === 'number' && Number.isInteger(value) && value >= q.scale.min && value <= q.scale.max
      if (ok) out[q.id] = value as number
      else if (q.required) return null
    } else if (q.type === 'multiple_choice') {
      const ok = typeof value === 'string' && q.options.includes(value)
      if (!ok) {
        if (q.required) return null
        continue
      }
      out[q.id] = value as string
      if (q.otherOption && value === q.otherOption) {
        const other = given[`${q.id}_other`]
        const text = typeof other === 'string' ? other.trim().slice(0, MAX_SURVEY_TEXT) : ''
        if (!text) return null
        out[`${q.id}_other`] = text
      }
    } else {
      const text = typeof value === 'string' ? value.trim().slice(0, q.maxLength ?? MAX_SURVEY_TEXT) : ''
      if (text) out[q.id] = text
      else if (q.required) return null
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// HTTP
// ---------------------------------------------------------------------------

/** `GET /api/study/config`. No codes, no secrets. */
export interface StudyPublicConfig {
  open: boolean
  /** Why the study is closed, in a sentence, when it is. */
  closedReason?: string
  allowTestPids: boolean
  games: number
  survey: StudySurveyQuestion[]
  /** How long before an idle deadline the warning appears. */
  idleWarningMs: number
  /** The fewest milliseconds between two `studyActivity` pings. */
  activityPingMs: number
  redirectCountdownMs: number
  heartbeatMs: number
  matchingTimeoutMs: number
  betweenGamesCountdownMs: number
  /** How long one survey submission waits for its acknowledgement. */
  surveyAckTimeoutMs: number
  /** How many times a survey submission is sent before the page gives up. */
  surveyMaxAttempts: number
  /** Study-specific consent wording, from `study.json`. */
  consent: {
    /** How long recordings and data are kept, as a phrase: "kept until …, then deleted". */
    retention: string
  }
}

/** `POST /api/study/stage-heartbeat`, answered. */
export interface StudyHeartbeat {
  paymentStage: StudyStage
  prolificRedirect: boolean
  completionCode: string | null
  redirectUrl: string | null
  /**
   * How the participant finished, or `null` while they are still going. Lets
   * a new tab show a finished participant their code before any device
   * check. Always `null` for a test id, which a rejoin starts over.
   */
  outcome: StudyOutcome | null
}

/**
 * Client events `POST /api/study/telemetry` accepts. Anything else is refused,
 * so the audit log cannot be filled with free text from a page.
 */
export const STUDY_TELEMETRY_TYPES = [
  'tech_check_passed',
  'tech_check_failed',
  'mic_permission_slow',
  'page_unload',
  'idle_warning_shown',
  'survey_submit_retry',
  'prolific_redirect_auto',
  'prolific_redirect_clicked',
  'heartbeat_handoff',
] as const

export type StudyTelemetryType = (typeof STUDY_TELEMETRY_TYPES)[number]

// ---------------------------------------------------------------------------
// Sockets
// ---------------------------------------------------------------------------

export interface StudyPhasePayload {
  phase: StudyPhase
  stage: StudyStage
  /** 1-based; the game being played or about to be, `null` before pairing. */
  gameIndex: number | null
  games: number
  /** Sticky: the partner left after a game had been played. */
  partnerDroppedMidGame: boolean
}

export interface StudyCompletePayload {
  /**
   * The stage's code, or at stage 0 the outcome's own (set to request a
   * return on Prolific). `null` only for a stage-0 ending stored before
   * stage-0 codes redirected.
   */
  completionCode: string | null
  reason: StudyOutcome
  paymentStage: StudyStage
  prolificRedirect: boolean
  redirectUrl: string | null
}

/**
 * `unavailable` is temporary: the server could not read the participant's
 * record and created nothing. Refreshing tries again.
 */
export type StudyRefusalReason = 'closed' | 'invalid' | 'test_pid_refused' | 'other_tab' | 'unavailable'

export interface StudyClientEvents {
  studyJoin: (payload: StudyParams) => void
  studyConsent: (payload: { accepted: boolean; consent?: ConsentRecord }) => void
  studyTutorialComplete: () => void
  /**
   * The solo microphone check and the latency gate, as one answer. `failure`
   * is `voice` for a microphone problem and `latency` for a connection the
   * game cannot be played on.
   */
  studyMicCheck: (payload: {
    passed: boolean
    failure?: 'voice' | 'latency'
    method?: 'auto' | 'manual'
    medianRttMs?: number
  }) => void
  studyActivity: () => void
  studySurveySubmit: (
    payload: { answers: StudySurveyAnswers },
    ack?: (result: { ok: boolean; error?: string }) => void,
  ) => void
}

export interface StudyServerEvents {
  studyPhase: (payload: StudyPhasePayload) => void
  studyQueue: (payload: { position: number; waitedMs: number; timeoutMs: number }) => void
  /** The server has seated this socket in a study room; `joined` came first. */
  studyGameReady: (payload: { roomCode: string; gameIndex: number; mapName: string }) => void
  /**
   * The gap between two games. `startsInMs` is relative to when the server
   * sent it, which is what the client counts down from; `startAt` is the
   * server's own epoch for the logs.
   */
  studyNextGameCountdown: (payload: {
    startAt: number
    startsInMs: number
    nextGameIndex: number
    completedGameIndex: number
    replay: boolean
  }) => void
  studyPartnerLost: (payload: { toSurvey: boolean; requeued: boolean }) => void
  /** `remainingMs: null` clears the deadline. */
  studyIdleDeadline: (payload: { phase: StudyIdlePhase | null; remainingMs: number | null }) => void
  studyComplete: (payload: StudyCompletePayload) => void
  studyRefused: (payload: { reason: StudyRefusalReason; message: string }) => void
}

// ---------------------------------------------------------------------------
// Session artifacts
// ---------------------------------------------------------------------------

/**
 * The optional `study` block on a session log header and manifest.
 *
 * Internal ids only. The PID ↔ participantId mapping lives in the study store,
 * which only the admin can read, so a manifest anybody can fetch says which
 * game of which dyad this was and nothing about who played it.
 */
export interface SessionStudyBlock {
  studySessionId: string
  /** 1-based. */
  gameIndex: number
  mapName: string
  /** This game repeats the previous game's map because it served nothing. */
  replayed: boolean
  participants: Record<PlayerId, { participantId: string }>
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

/** One participant, as stored and as the admin audit table shows it. */
export interface StudyParticipantRecord {
  participantId: string
  prolificPid: string
  studyId: string
  /**
   * Prolific's `SESSION_ID`, which is the submission id: the latest one seen
   * while the record was active, frozen once it ends. Earlier ones stay in
   * the audit trail as `session_id_changed`.
   */
  prolificSessionId: string
  test: boolean
  studySessionId: string | null
  partnerParticipantId: string | null
  status: StudyStatus
  phase: StudyPhase
  highestStage: StudyStage
  completionCode: string | null
  prolificRedirect: boolean
  partnerDroppedMidGame: boolean
  rescueRequeues: number
  /** Milestones, ISO. */
  consentAt: string | null
  /** The `STUDY_CONSENT_VERSION` agreed to; `null` before consent (and on a withdrawn stub). */
  consentVersion: number | null
  tutorialAt: string | null
  micAt: string | null
  queuedAt: string | null
  pairedAt: string | null
  gameStartedAt: Record<string, string>
  gameEndedAt: Record<string, string>
  /**
   * The session id of every game round started with this participant in it,
   * in order (a rescue requeue can start a second game 1, so not by game
   * index). Kept here as well as in the audit trail, whose writes are not
   * waited on, so a withdrawal finds every session even when a trail write
   * was lost. Empty on a withdrawn stub, and on records from before it.
   */
  gameSessionIds: string[]
  surveyAt: string | null
  completedAt: string | null
  /** Milliseconds spent in the waiting room, summed over every wait. */
  waitedMs: number
  saveFailed: boolean
  createdAt: string
  updatedAt: string
  /**
   * When the admin withdrew the participant and deleted their recordings,
   * survey and audit trail. A withdrawn record is a stub: only the review
   * fields are kept, and `participantId` is blank, so nothing stored links
   * the Prolific id to the sessions any more.
   */
  withdrawnAt: string | null
}

/**
 * One row of the admin's manual-review table, derived from a record alone.
 * Prolific pays STAGE4 by itself; the owner reviews everyone else by hand and
 * pays them for their time from this row. No amounts: the app computes no pay.
 */
export interface StudyReviewRow {
  prolificPid: string
  /** The submission id (`SESSION_ID`) the owner reviews on Prolific. */
  prolificSessionId: string
  studyId: string
  test: boolean
  /** The outcome, or `active` while the participant is still in the study. */
  status: StudyStatus
  highestStage: StudyStage
  completionCode: string | null
  startedAt: string
  /** `null` while active. */
  finishedAt: string | null
  /** From start to finish, or to the moment of the listing while active. */
  timeInStudyMs: number
  /** Time spent waiting for a partner, summed over every wait. */
  waitedMs: number
  /** Set once the participant was withdrawn and their data deleted. */
  withdrawnAt: string | null
}

/** `POST /api/admin/study/participants/:pid/withdraw`, answered. */
export interface StudyWithdrawal {
  /** The stub that is kept. */
  review: StudyReviewRow
  /** What was deleted just now: zeros for a participant withdrawn before. */
  deleted: { audio: number; surveys: number; events: boolean }
  alreadyWithdrawn: boolean
}

export interface StudyLiveSnapshot {
  open: boolean
  closedReason: string | null
  placeholderCodes: string[]
  queue: Array<{ participantId: string; shortPid: string; waitedMs: number; connected: boolean }>
  solo: Array<{ participantId: string; shortPid: string; phase: StudyPhase; elapsedMs: number; connected: boolean }>
  dyads: Array<{
    studySessionId: string
    roomCode: string
    gameIndex: number
    mapName: string
    phase: string
    participants: string[]
    partnerLost: boolean
  }>
}

/** An event in a participant's audit trail. */
export interface StudyAuditEvent {
  at: string
  type: string
  [detail: string]: unknown
}
